import useSWR from 'swr';
import {
  SWR_CONFIG,
  DUNE_QUERIES,
  METRICS_URL,
  STATIC_FALLBACK_METRICS
} from '../constants/duneConfig';
import { formatMetric } from '../utils/metricFormatters';

/**
 * Fetches every product figure in one call to the serverless function (api/metrics.js),
 * which holds the Dune key server-side. The browser never talks to Dune.
 * Locally under `yarn start` there is no /api, so the call fails and the cards fall back.
 */
const metricsFetcher = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Metrics endpoint returned ${response.status}`);
  return response.json();
};

/**
 * Hook for the shared metrics payload: { soroswap: { TVL, accounts }, defindex: { TVL, accounts } }
 * @returns {Object} SWR result with loading and error states
 */
export const useMetrics = () => {
  const { data, error, isLoading, mutate } = useSWR(METRICS_URL, metricsFetcher, SWR_CONFIG);
  return { data, error, isLoading, refetch: mutate, isError: !!error };
};

/**
 * Hook for protocol metrics with fallback system
 * @param {string} protocol - Protocol name (soroswap, defindex)
 * @returns {Object} Protocol metrics with fallback handling
 */
export const useProtocolMetrics = (protocol) => {
  const staticFallbacks = STATIC_FALLBACK_METRICS[protocol];

  if (!DUNE_QUERIES[protocol]) {
    throw new Error(`Protocol "${protocol}" not found in configuration`);
  }

  const metricsQuery = useMetrics();

  const getMetric = (metricType) => {
    if (metricsQuery.isLoading) {
      return { isLoading: true, formatted: null };
    }

    const value = metricsQuery.data?.[protocol]?.[metricType];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return {
        isLoading: false,
        value,
        formatted: formatMetric(metricType, value),
        isRealTime: true
      };
    }

    // Fallback to static data
    return {
      isLoading: false,
      value: staticFallbacks[metricType].value,
      formatted: staticFallbacks[metricType].formatted,
      isRealTime: false
    };
  };

  return {
    tvl: getMetric('TVL'),
    accounts: getMetric('accounts'),
    isLoading: metricsQuery.isLoading,
    hasError: metricsQuery.isError,
    protocol
  };
};

/**
 * Hook for all protocols data
 * @returns {Object} All protocols metrics
 */
export const useAllProtocolsData = () => {
  const soroswap = useProtocolMetrics('soroswap');
  const defindex = useProtocolMetrics('defindex');

  return {
    soroswap,
    defindex,
    isLoading: soroswap.isLoading || defindex.isLoading,
    hasError: soroswap.hasError || defindex.hasError
  };
};
